import type { KeycloakConfig } from './config'
import { keycloakConfig, REDIRECT_URI } from './config'
import type { OAuthParams, TokenPair } from './types'

export function parseOAuthParams(): OAuthParams {
  const hash = window.location.hash.substring(1)
  const hashParams = new URLSearchParams(hash)
  if (hashParams.get('code') || hashParams.get('state') || hashParams.get('error')) {
    return {
      code: hashParams.get('code') || undefined,
      state: hashParams.get('state') || undefined,
      error: hashParams.get('error') || undefined,
      errorDescription: hashParams.get('error_description') || undefined,
      source: 'hash',
    }
  }

  const queryParams = new URLSearchParams(window.location.search)
  if (queryParams.get('code') || queryParams.get('state') || queryParams.get('error')) {
    return {
      code: queryParams.get('code') || undefined,
      state: queryParams.get('state') || undefined,
      error: queryParams.get('error') || undefined,
      errorDescription: queryParams.get('error_description') || undefined,
      source: 'query',
    }
  }

  return { source: 'none' }
}

export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  config: KeycloakConfig = keycloakConfig
): Promise<TokenPair> {
  const tokenUrl = `${config.url}/realms/${config.realm}/protocol/openid-connect/token`

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    code_verifier: codeVerifier,
    redirect_uri: REDIRECT_URI,
  })

  console.log('[Auth] Exchanging code for tokens', { tokenUrl, hasCodeVerifier: !!codeVerifier })

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('[Auth] Token exchange failed', { status: response.status, error: errorText })
    throw new Error(`Token exchange failed: ${response.status} - ${errorText}`)
  }

  const data = await response.json()
  console.log('[Auth] Token exchange successful', { expiresIn: data.expires_in })

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    idToken: data.id_token,
    expiresIn: data.expires_in,
  }
}

export async function refreshAccessToken(
  refreshToken: string,
  config: KeycloakConfig = keycloakConfig
): Promise<TokenPair> {
  const tokenUrl = `${config.url}/realms/${config.realm}/protocol/openid-connect/token`

  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  })

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`)
  }

  const data = await response.json()
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  }
}
